import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import BaseWorkflowsList from './BaseWorkflowsList';
import SlugWorkflowsList from './SlugWorkflowsList';
import OverrideDefinitionsManager from './OverrideDefinitionsManager';
import UserManagement from './UserManagement';

type TabType = 'base' | 'slug' | 'overrides' | 'users';

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('base');

  if (!user || (user.role !== 'super_admin' && user.role !== 'admin')) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 text-lg font-semibold">Access Denied</div>
        <p className="text-gray-600 mt-2">You need admin privileges to access this page.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
        <p className="text-gray-600">Manage workflows, override definitions, users, and system configuration</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('base')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'base'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <span className="text-lg">📄</span>
              Base Workflows (GitHub)
            </span>
          </button>

          <button
            onClick={() => setActiveTab('slug')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'slug'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <span className="text-lg">⚙️</span>
              Slug Workflows (EOK)
            </span>
          </button>

          <button
            onClick={() => setActiveTab('overrides')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'overrides'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <span className="text-lg">🎛️</span>
              Override Definitions
            </span>
          </button>

          <button
            onClick={() => setActiveTab('users')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <span className="text-lg">👥</span>
              User Management
            </span>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {activeTab === 'base' && <BaseWorkflowsList />}
        {activeTab === 'slug' && <SlugWorkflowsList />}
        {activeTab === 'overrides' && <OverrideDefinitionsManager />}
        {activeTab === 'users' && <UserManagement />}
      </div>
    </div>
  );
};

export default AdminDashboard;
